import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAuth } from "./AuthContext";
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  deleteNotification,
  markAllNotificationsRead,
  deleteAllNotifications,
} from "../services/notificationService";
import { connectNotificationHub, disconnectNotificationHub, subscribeLiveNotification } from "../services/signalrService";

const NotificationContext = createContext(null);
const PAGE_SIZE = 20;

export const NotificationProvider = ({ children }) => {
  const { t } = useTranslation("notifications");
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [meta, setMeta] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const categoryFilterRef = useRef(undefined);

  const refreshCount = useCallback(() => {
    getUnreadCount()
      .then((res) => setUnreadCount(res.data?.Result?.Count ?? 0))
      .catch(() => {});
  }, []);

  // Always replaces the list for the requested page (proper pagination, not
  // infinite-append) — the only page>1 caller left is NotificationsPage's
  // own pager, and NotificationBell only ever asks for page 1, so this is
  // safe for both consumers.
  const load = useCallback((pageNumber = 1, categoryIds = categoryFilterRef.current) => {
    categoryFilterRef.current = categoryIds;
    (pageNumber === 1 ? setLoading : setLoadingMore)(true);
    return getNotifications({ pageNumber, pageSize: PAGE_SIZE, categoryIds })
      .then((res) => {
        setNotifications(res.data?.Result?.Items || []);
        setMeta(res.data?.Metadata || null);
        setPage(pageNumber);
        // GET /Notification/User marks every returned item IsSeen=true on
        // the backend as a side effect (confirmed in
        // WebAPI/Controllers/NotificationController.cs) — refresh the badge
        // count right away so it reflects that immediately.
        refreshCount();
      })
      .catch(() => toast.error(t("loadError")))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, [t, refreshCount]);

  const markRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.Id === id ? { ...n, IsRead: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    return markNotificationRead(id).catch(() => {});
  }, []);

  const removeOne = useCallback((id) => {
    const wasUnread = notifications.find((n) => n.Id === id && !n.IsRead);
    setNotifications((prev) => prev.filter((n) => n.Id !== id));
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));
    return deleteNotification(id)
      .then(() => toast.success(t("deleteSuccess")))
      .catch(() => toast.error(t("deleteError")));
  }, [notifications, t]);

  const markAllRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, IsRead: true })));
    setUnreadCount(0);
    return markAllNotificationsRead()
      .then(() => toast.success(t("markAllReadSuccess")))
      .catch(() => toast.error(t("markAllReadError")));
  }, [t]);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    return deleteAllNotifications()
      .then(() => toast.success(t("deleteAllSuccess")))
      .catch(() => toast.error(t("deleteAllError")));
  }, [t]);

  // Badge count only on mount/login — deliberately NOT calling load(1) here.
  // GET /Notification/User marks everything it returns as "seen" server-side
  // (see the load() comment above), so eagerly fetching the list in the
  // background would zero the unread badge before the user ever opens the
  // bell to look at it. The list itself loads lazily instead — see
  // NotificationBell's open-effect and NotificationsPage's mount effect.
  useEffect(() => {
    if (!token) {
      setNotifications([]);
      setUnreadCount(0);
      setMeta(null);
      return;
    }
    refreshCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Live delivery: the backend pushes "ReceiveLiveNotification" over the chat
  // hub (see signalrService.js for why). Its payload isn't the full
  // NotificationReceiver shape the list needs, so treat it as a "something
  // new arrived" signal — toast immediately and refresh just the badge
  // count (not the list — same mark-seen-on-fetch reasoning as above).
  useEffect(() => {
    if (!token) {
      disconnectNotificationHub();
      return;
    }

    connectNotificationHub(() => localStorage.getItem("token") || "");
    const unsubscribe = subscribeLiveNotification((payload) => {
      if (!payload?.muted && (payload?.title || payload?.body)) {
        toast.info(payload.title || payload.body);
      }
      refreshCount();
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => () => disconnectNotificationHub(), []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        meta,
        page,
        pageSize: PAGE_SIZE,
        loading,
        loadingMore,
        load,
        markRead,
        removeOne,
        markAllRead,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);
